"use client";

import { Star } from "lucide-react";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

type ReviewStarsProps = {
  rating: number;
  size?: "sm" | "md" | "lg";
  showValue?: boolean;
  className?: string;
};

const sizeClass = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-7 w-7",
} as const;

const clampRating = (value: number) =>
  Math.max(0, Math.min(5, Number.isFinite(value) ? value : 0));

const formatRating = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

export const toWholeStarRating = (value: number) =>
  Math.ceil(clampRating(value));

export function ReviewStars({
  rating,
  size = "md",
  showValue = false,
  className,
}: ReviewStarsProps) {
  const safeRating = clampRating(rating);

  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span className="inline-flex items-center gap-0.5" aria-hidden>
        {[0, 1, 2, 3, 4].map((index) => {
          const fill = Math.max(0, Math.min(1, safeRating - index)) * 100;
          return (
            <span key={index} className={cn("relative", sizeClass[size])}>
              <Star
                className={cn(
                  "absolute inset-0 text-border dark:text-white/20",
                  sizeClass[size],
                )}
                strokeWidth={1.8}
              />
              <span
                className="absolute inset-0 overflow-hidden text-amber-500"
                style={{ width: `${fill}%` }}
              >
                <Star
                  className={cn("fill-current", sizeClass[size])}
                  strokeWidth={1.8}
                />
              </span>
            </span>
          );
        })}
      </span>
      <span className="sr-only">{formatRating(safeRating)} out of 5 stars</span>
      {showValue ? (
        <span className="text-sm font-semibold tabular-nums dark:text-white">
          {formatRating(safeRating)}
        </span>
      ) : null}
    </span>
  );
}

type ReviewStarInputProps = {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the slider (e.g. the subrating category). */
  ariaLabel?: string;
};

const RATING_LABELS = [
  "Terrible",
  "Poor",
  "Okay",
  "Good",
  "Excellent",
] as const;

function ratingLabel(value: number) {
  if (value <= 0) return "Select a rating";
  const index = Math.min(4, Math.max(0, value - 1));
  return `${value} · ${RATING_LABELS[index]}`;
}

/**
 * Whole-star rating control. Each star's full hit area selects that star,
 * including on touch. Arrow / Home / End keys select whole stars too.
 */
export function ReviewStarInput({
  value,
  onChange,
  disabled,
  className,
  ariaLabel = "Overall rating",
}: ReviewStarInputProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [pulseValue, setPulseValue] = useState<number | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const displayValue = hoverValue ?? toWholeStarRating(value);

  const valueFromClientX = (clientX: number) => {
    const row = rowRef.current;
    if (!row) return toWholeStarRating(value) || 1;
    const stars = Array.from(row.children);
    for (const [index, star] of stars.entries()) {
      if (clientX <= star.getBoundingClientRect().right) return index + 1;
    }
    return 5;
  };

  const commit = (next: number) => {
    if (disabled) return;
    onChange(next);
    setPulseValue(next);
    window.setTimeout(() => setPulseValue(null), 240);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    let next: number | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = Math.min(5, toWholeStarRating(value) + 1);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        next = Math.max(1, (toWholeStarRating(value) || 2) - 1);
        break;
      case "Home":
        next = 1;
        break;
      case "End":
        next = 5;
        break;
      default:
        return;
    }
    event.preventDefault();
    commit(next);
  };

  return (
    <fieldset
      className={cn("flex flex-wrap items-center gap-3", className)}
      disabled={disabled}
    >
      <legend className="sr-only">
        Select a rating from 1 to 5 whole stars
      </legend>
      <div
        ref={rowRef}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-valuenow={toWholeStarRating(value)}
        aria-valuetext={
          value > 0 ? `${toWholeStarRating(value)} stars` : "No rating selected"
        }
        aria-disabled={disabled || undefined}
        className="inline-flex touch-none items-center gap-1 rounded-lg transition outline-none select-none focus-visible:ring-2 focus-visible:ring-amber-500/50 disabled:opacity-50"
        onKeyDown={handleKeyDown}
        onPointerDown={(event) => {
          if (disabled) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          const next = valueFromClientX(event.clientX);
          setHoverValue(next);
          commit(next);
        }}
        onPointerMove={(event) => {
          if (disabled) return;
          if (event.pointerType === "mouse" || event.buttons > 0) {
            setHoverValue(valueFromClientX(event.clientX));
          }
        }}
        onPointerUp={(event) => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
          }
          setHoverValue(null);
        }}
        onPointerLeave={() => setHoverValue(null)}
      >
        {[0, 1, 2, 3, 4].map((index) => {
          const filled = displayValue > index;
          const numericValue = index + 1;
          const pulsing =
            pulseValue != null &&
            Math.ceil(pulseValue) === numericValue &&
            pulseValue > index;

          return (
            <span
              key={numericValue}
              aria-hidden
              className={cn(
                "relative block h-9 w-9 text-border dark:text-white/25",
                pulsing &&
                  "motion-safe:animate-[review-star-pop_200ms_ease-out]",
              )}
            >
              <Star className="absolute inset-1 h-7 w-7" strokeWidth={1.9} />
              <span
                className="absolute inset-1 overflow-hidden text-amber-500"
                style={{ width: filled ? "100%" : "0%" }}
              >
                <Star className="h-7 w-7 fill-current" strokeWidth={1.9} />
              </span>
            </span>
          );
        })}
      </div>
      <style jsx>{`
        @keyframes review-star-pop {
          0% {
            transform: scale(1);
          }
          55% {
            transform: scale(1.18);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
      <output className="min-w-[7rem] text-sm font-medium text-text-secondary dark:text-white/70">
        {ratingLabel(displayValue)}
      </output>
    </fieldset>
  );
}
